import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router, RouterEvent } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { AdminUsersAlertService, AdminUsersCustomerService, AdminUsersRoutesService } from '@nx-ratenow/admin-users-services';
import { UserFacade } from '@nx-ratenow/core/data-access-user';
import { environment } from '@nx-ratenow/core/environment';
import { AuthService } from '@nx-ratenow/core/feature-auth';
import { ToasterService } from '@nx-ratenow/core/feature-notifications';
import { AppsMenuComponent } from '@nx-ratenow/core/layout/feature-toolbar';
import { PopoverService } from '@nx-ratenow/shared/feature-popover';
import { PersonAuthDetailsDto } from '@nx-ratenow/shared/models';
import { collapseOnLeaveAnimation, expandOnEnterAnimation } from 'angular-animations';
import { map, Observable, of, startWith, Subject, switchMap, takeUntil } from 'rxjs';

@Component({
  standalone: false,
  selector: 'nx-admin-users-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrl: './main-layout.component.scss',
  animations: [expandOnEnterAnimation({ duration: 400 }), collapseOnLeaveAnimation({ duration: 400 })]
})
export class MainLayoutComponent implements OnInit, OnDestroy {
  isExpanded: boolean = true;
  userLogged: PersonAuthDetailsDto;
  breadcrumb: any[];
  routeSelected: string;
  megaMenuOpen$: Observable<boolean> = of(false);
  apps: any;
  loading: boolean = false;
  unsubscribe = new Subject<void>();

  urlNotVariables = [
    'dashboard',
    'users',
    'devices',
    'info',
    'organization',
    'data-access',
    'operation',
    'survey-group',
    'alerts',
    'visuals',
    'documentation',
    'account-settings',
    'about',
    'client-assistant',
    'password',
    'changelog',
    'assign-device',
    'user-groups'
  ];

  menuItems: any[] = [
    /*{
      name: 'Dashboard',
      url: '/dashboard',
      icon: 'diversity_2',
      disabled: false,
      expanded: false,
      children: []
    },*/
    {
      name: 'admin-users.sidenav.users',
      url: '/users',
      icon: 'person_search',
      disabled: false,
      expanded: false,
      hidden: false,
      authority: 'CFG_VIEW_USER',
      children: [
        {
          name: 'admin-users.sidenav.user-info',
          buildUrl: () => this.buildUrl('/users', '/info'),
          url: '/info',
          icon: 'person',
          hidden: false,
          authority: 'CFG_EDIT_USER'
        },
        {
          name: 'admin-users.sidenav.alert-logs',
          buildUrl: () => this.buildUrl('/users', '/logs'),
          url: '/logs',
          icon: 'history',
          hidden: false,
          authority: 'CFG_VIEW_ALERT'
        },
        {
          name: 'admin-users.sidenav.roles',
          buildUrl: () => this.buildUrl('/users', '/roles'),
          url: '/roles',
          icon: 'admin_panel_settings',
          hidden: false,
          authority: 'CFG_EDIT_USER'
        },
        {
          name: 'admin-users.sidenav.data-access',
          buildUrl: () => this.buildUrl('/users', '/data-access'),
          url: '/data-access',
          icon: 'dataset',
          authority: 'CFG_VIEW_GROUPS',
          hidden: true
        }
      ]
    },
    {
      name: 'admin-users.sidenav.user-groups',
      url: '/user-groups',
      icon: 'groups',
      disabled: false,
      expanded: false,
      hidden: false,
      authority: 'CFG_VIEW_GROUP_USERS',
      children: [
        {
          name: 'admin-users.sidenav.user-groups-info',
          buildUrl: () => this.buildUrl('/user-groups', '/info'),
          url: '/info',
          icon: 'group',
          authority: 'CFG_EDIT_GROUP_USERS',
          hidden: false
        },
        {
          name: 'admin-users.sidenav.data-access',
          buildUrl: () => this.buildUrl('/user-groups', '/data-access'),
          url: '/data-access',
          icon: 'dataset',
          authority: 'CFG_VIEW_GROUPS',
          hidden: true
        }
      ]
    },
    {
      name: 'admin-users.sidenav.devices',
      url: '/devices',
      icon: 'important_devices',
      disabled: false,
      expanded: false,
      hidden: false,
      authority: 'CFG_VIEW_DEVICE',
      children: [
        {
          name: 'admin-users.sidenav.device-info',
          buildUrl: () => this.buildUrl('/devices', '/info'),
          url: '/info',
          icon: 'perm_device_information',
          authority: 'CFG_EDIT_DEVICE',
          hidden: false
        },
        /*{
          name: 'Operación',
          buildUrl: () => this.buildUrl('/devices', '/operation'),
          url: '/operation',
          icon: 'access_time'
        },*/
        {
          name: 'admin-users.sidenav.survey-groups',
          buildUrl: () => this.buildUrl('/devices', '/survey-groups'),
          url: '/survey-groups',
          icon: 'assignment_turned_in',
          authority: 'CFG_EDIT_DEVICE',
          hidden: false
        },
        {
          name: 'admin-users.sidenav.alert-logs',
          buildUrl: () => this.buildUrl('/devices', '/logs'),
          url: '/logs',
          icon: 'history',
          hidden: false,
          authority: 'CFG_EDIT_DEVICE'
        }
        /*{
          name: 'Alertas',
          buildUrl: () => this.buildUrl('/devices', '/alerts'),
          url: '/alerts',
          icon: 'notifications'
        },
        {
          name: 'Visualización',
          buildUrl: () => this.buildUrl('/devices', '/visuals'),
          url: '/visuals',
          icon: 'remove_red_eye'
        }*/
      ]
    },
    {
      name: 'admin-users.sidenav.alerts',
      url: '/alerts',
      icon: 'notifications',
      disabled: false,
      expanded: false,
      hidden: false,
      authority: 'CFG_VIEW_ALERT',
      children: [
        {
          name: 'admin-users.sidenav.info-alerts',
          buildUrl: () => this.buildUrl('/alerts', '/info'),
          url: '/info',
          icon: 'info',
          authority: 'CFG_EDIT_ALERT',
          hidden: false
        },
        {
          name: 'admin-users.sidenav.alert-logs',
          buildUrl: () => this.buildUrl('/alerts', '/logs'),
          url: '/logs',
          icon: 'history',
          authority: 'CFG_EDIT_ALERT',
          hidden: false
        },
        {
          name: 'admin-users.sidenav.users-subscribed',
          buildUrl: () => this.buildUrl('/alerts', '/subscribed-users'),
          url: '/subscribed-users',
          icon: 'group',
          authority: 'CFG_EDIT_ALERT',
          hidden: false
        },
        {
          name: 'admin-users.sidenav.devices-assigned',
          buildUrl: () => this.buildUrl('/alerts', '/assigned-devices'),
          url: '/assigned-devices',
          icon: 'devices',
          authority: 'CFG_EDIT_ALERT',
          hidden: false
        }
      ]
    },
    {
      name: 'admin-users.sidenav.questionnaires',
      url: `https://edit-surveys${environment.name}.ratenow.es`,
      icon: 'question_answer',
      disabled: false,
      expanded: false,
      hidden: false,
      children: [],
      authority: 'SED_LOGIN'
    },
    {
      name: 'admin-users.sidenav.organization',
      url: '/organization',
      icon: 'corporate_fare',
      disabled: false,
      expanded: false,
      hidden: false,
      authority: 'CFG_VIEW_GROUPS',
      children: []
    }
  ];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private routesService: AdminUsersRoutesService,
    private popoverService: PopoverService,
    private authService: AuthService,
    private userFacade: UserFacade,
    private toasterService: ToasterService,
    private applicationsService: AdminUsersCustomerService,
    public translateService: TranslateService,
    private alertService: AdminUsersAlertService
  ) {
    this.route.data.subscribe((data) => {
      this.userLogged = data['userLoggedInfo'];
      this.translateService.use(this.userLogged.personLanguage);
      if (localStorage.getItem('LANG')) {
        this.translateService.use(localStorage.getItem('LANG'));
      }
    });

    this.router.events.pipe(takeUntil(this.unsubscribe)).subscribe((r: any) => {
      if (r instanceof NavigationEnd) {
        this.routeSelected = r.urlAfterRedirects;
        const splitted = (this.breadcrumb = this.routeSelected.split('/'));
        if (splitted[3]) {
          const found = this.menuItems.findIndex((menuItem) => '/' + splitted[1] === menuItem.url);
          this.menuItems.map((menuItem, index) => (menuItem.expanded = index === found));
        } else {
          this.menuItems.map((menuItem) => (menuItem.expanded = false));
        }
      }
      this.checkRouterEvent(r);
    });
  }

  ngOnInit(): void {
    this.applicationsService.getCustomerInfo().subscribe((apps) => {
      this.apps = apps;
    });
    this.hiddenMenuByAuthority();
    this.alertService.alert.subscribe((alert) => {
      if (alert) {
        let menuAlerts = this.menuItems.find((item) => item.url === '/alerts');
        menuAlerts.children.find((item) => item.url === '/assigned-devices').hidden = alert.type === 'QN';
      }
    });
  }

  checkRouterEvent(routerEvent: RouterEvent): void {
    if (routerEvent instanceof NavigationStart) {
      this.loading = true;
    }

    if (routerEvent instanceof NavigationEnd || routerEvent instanceof NavigationCancel || routerEvent instanceof NavigationError) {
      this.loading = false;
    }
  }

  hiddenMenuByAuthority() {
    this.menuItems.forEach((menuItem: any) => {
      if (menuItem.children.length !== 0) {
        menuItem.children.forEach((submenuItem: any) => {
          if (this.userLogged.authorities.includes(submenuItem.authority)) {
            submenuItem.hidden = false;
          } else {
            submenuItem.hidden = true;
          }
        });
      }

      if (this.userLogged.authorities.includes(menuItem.authority)) {
        menuItem.hidden = false;
      } else {
        menuItem.hidden = true;
      }
    });
  }

  buildUrlHastaAqui(r) {
    const splitted = this.routeSelected.split('/');
    const foundIndex = splitted.findIndex((spl) => spl === r);
    let i = 1;
    let newRoute = '';
    while (i <= foundIndex) {
      newRoute += '/' + splitted[i];
      i++;
    }
    this.goUrl(newRoute);
  }

  getRouteText(r) {
    if (this.urlNotVariables.includes(r)) {
      return r;
    } else {
      const item = this.routesService.mapRoutes.get(this.breadcrumb[this.breadcrumb.findIndex((bread) => bread === r) - 1]);
      return item ? item.name : r;
    }
  }

  goUrl(url) {
    if (url.includes('edit-surveys')) {
      window.open(url, '_blank');
    } else {
      this.alertService.clear(); // limpiamos el alert para el submenu hidden
      this.router.navigateByUrl(url);
    }
  }

  buildUrl(url, subUrl) {
    return url + '/' + this.routesService.routeId.getValue() + subUrl;
  }

  goSubUrl(fn) {
    this.router.navigateByUrl(fn.call());
  }

  openMegaMenu(origin: ElementRef | HTMLElement): void {
    this.megaMenuOpen$ = of(
      this.popoverService.open({
        data: { applications: this.apps.applications },
        content: AppsMenuComponent,
        origin,
        offsetY: 12,
        position: [
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top'
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top'
          }
        ]
      })
    ).pipe(
      switchMap((popoverRef) => popoverRef.afterClosed$.pipe(map(() => false))),
      startWith(true)
    );
  }

  logOut(): void {
    this.authService.logout().subscribe(() => {
      this.userFacade.logOut();
      this.toasterService.warning('login.com-back-soon');
    });
    // this.close();
  }

  changeLang(lang: string) {
    this.translateService.use(lang);
    localStorage.setItem('LANG', lang);
  }

  // close(): void {
  //   /** Wait for animation to complete and then close */
  //   setTimeout(() => this.popoverRef.close(), 250);
  // }
  ngOnDestroy(): void {
    this.unsubscribe.next();
  }
}
